import { lazy, Suspense, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Divider, InputWithLabel, Skeleton, TextArea, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import AppContext from '@/common/context/app.context'
import { getAvailableEmailVariables, getAvailableEmailVariablesSections } from '@/common/helpers/helpers'
import useCarriers from '@/common/hooks/useCarriers/useCarriers'
import useCarrierServices from '@/common/hooks/useCarrierServices/useCarrierServices'
import useLocations from '@/common/hooks/useLocations/useLocations'
import useMerchantFeatures from '@/common/hooks/useMerchantFeatures/useMerchantFeatures'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import useReturnReasons from '@/common/hooks/useReturnReasons/useReturnReasons'
import useRuleFeatures from '@/common/hooks/useRuleFeatures/useRuleFeatures'
import useStoreCurrencySymbol from '@/common/hooks/useStoreCurrencySymbol/useStoreCurrencySymbol'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import useStoreProductTypes from '@/common/hooks/useStoreProductTypes/useStoreProductTypes'
import { settingsTypes } from '@/configuration/constants'
import Preview from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/components/Preview/Preview'
import { sectionTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import useSettingsType from '@/configuration/pages/ConfigurationPage/hooks/useSettingsType/useSettingsType'

import Content from './Content/Content'
import ContentBlocks from './ContentBlocks/ContentBlocks'
import { contentTabs } from './ContentTabs/constants'
import ContentTabs from './ContentTabs/ContentTabs'
import CustomHtmlUpsell from './CustomHtmlUpsell/CustomHtmlUpsell'
import { editorModes } from './EditorModeSelector/constants'
import EditorModeSelector from './EditorModeSelector/EditorModeSelector'
import EmailTemplateConditionsEditor from './EmailTemplateConditionsEditor/EmailTemplateConditionsEditor'
import EmailTemplateDetailSkeleton from './EmailTemplateDetailSkeleton/EmailTemplateDetailSkeleton'
import GlobalSettingsInUseBanner from './GlobalSettingsInUseBanner/GlobalSettingsInUseBanner'
import useContentTab from './hooks/useContentTab/useContentTab'
import useCustomHtmlUpsell from './hooks/useCustomHtmlUpsell/useCustomHtmlUpsell'
import useEmailTemplateDetail from './hooks/useEmailTemplateDetail/useEmailTemplateDetail'
import usePreview from './hooks/usePreview/usePreview'
import LanguageTabs from './LanguageTabs/LanguageTabs'
import { liquidEditorHeight } from './LiquidEditor/constants'
import LiquidPreview from './LiquidPreview/LiquidPreview'
import Preheader from './Preheader/Preheader'
import Subject from './Subject/Subject'
import Trigger from './Trigger/Trigger'
import * as Styles from './styles'

const LiquidEditor = lazy(() => import('./LiquidEditor/LiquidEditor'))

const EmailTemplateDetailConfiguration = ({ isUnifiedDashboard = false }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { id } = useParams()
  const navigate = useNavigate()
  const { setToastInfo } = useContext(ToastContext)
  const { settingsType } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.email_templates,
    isUnifiedDashboard,
    setToastInfo,
  })
  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })
  const { currencies, storeCurrencyCode } = useStoreCurrencySymbol()
  const {
    availableLanguages,
    languageSelected,
    handleChangeLanguage,
    subject,
    subjectText,
    handleChangeSubject,
    preheader,
    handleChangePreheader,
    mode,
    handleChangeMode,
    content,
    handleChangeContent,
    handleChangeLiquidContent,
    liquidError,
    name,
    description,
    handleChangeName,
    handleChangeDescription,
    onSaveChanges,
    onCancelChanges,
    fetchingTemplate,
    hasChanges,
    editingDetailEmailTemplate,
    isFormValid,
    isNewTemplate,
    blocks,
    handleChangeBlocks,
    triggers,
    handleChangeTrigger,
    handleAddTrigger,
    handleRemoveTrigger,
    conditions,
    conditionErrors,
    handleChangeConditions,
    errors,
  } = useEmailTemplateDetail({
    storeSupportedLanguages: info?.selectedStore?.supported_languages,
    storeId: info?.selectedStore?.id,
    templateId: id,
    navigate,
    setToastInfo,
    configurationMode,
    merchantId: info?.id,
    isUnifiedDashboard,
    returnPortalId: info?.selectedReturnPortal?.id,
    storeCurrencyCode,
  })
  const { selectedContentTab, setSelectedContentTab } = useContentTab()
  const { preview, fetchingPreview, liquidPreviewError, isLiquidContentEmpty } = usePreview({
    subjectText,
    content,
    language: languageSelected,
    mode,
    storeId: info?.selectedStore?.id,
    setToastInfo,
    selectedContentTab,
    configurationMode,
    merchantId: info?.id,
    blocks,
    returnPortalId: info?.selectedReturnPortal?.id,
  })
  const { productTypes } = useStoreProductTypes({ storeId: info?.selectedStore?.id, doInitialRequest: true })
  const { productCategories } = useStoreProductCategories({ storeId: info?.selectedStore?.id, doInitialRequest: true })
  const { productCollections } = useStoreProductCollections({
    storeId: info?.selectedStore?.id,
    doInitialRequest: true,
  })
  const { regions, loading: fetchingRegions } = useRegions()
  const { locations, fetchingLocations } = useLocations({ storeId: info?.selectedStore?.id })
  const { carriers, fetchingCarriers } = useCarriers({ storeId: info?.selectedStore?.id, doInitialRequest: true })
  const { carrierServices, fetchingCarrierServices } = useCarrierServices({
    storeId: info?.selectedStore?.id,
    section: 'shipments',
  })
  const { returnReasons, fetchingReturnReasons } = useReturnReasons({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: true,
  })
  const { disabledConditionFields, disabledTriggers, fetchingRuleFeatures } = useRuleFeatures({
    storeId: info?.selectedStore?.id,
  })
  const { returnPortalFeatures, fetchingReturnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: !isUnifiedDashboard,
  })
  const { merchantFeatures, fetchingMerchantFeatures } = useMerchantFeatures({
    merchantId: info?.id,
    doInitialRequest: isUnifiedDashboard,
  })
  const features = isUnifiedDashboard ? merchantFeatures : returnPortalFeatures
  const isLiquidMode = mode === editorModes.liquid
  const availableVariables = getAvailableEmailVariables(
    ['returns', 'shipments'],
    triggers.map(trigger => trigger.type)
  )
  const availableSections = getAvailableEmailVariablesSections(['returns', 'shipments'])
  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global
  const isLoading =
    fetchingTemplate ||
    fetchingRegions ||
    fetchingLocations ||
    fetchingCarriers ||
    fetchingCarrierServices ||
    fetchingReturnReasons ||
    fetchingRuleFeatures ||
    fetchingReturnPortalFeatures ||
    fetchingMerchantFeatures

  const handleChangeEditorMode = newMode => {
    handleChangeMode(newMode)
    setSelectedContentTab(contentTabs.write)
  }

  const { selectedEditorMode, isCustomHtmlUpsellShown, handleSelectEditorMode, handleBackToStandardEditor } =
    useCustomHtmlUpsell({
      mode,
      smartTrackingEnabled: features?.smart_tracking_enabled,
      onChangeMode: handleChangeEditorMode,
    })

  if (isLoading) {
    return <EmailTemplateDetailSkeleton />
  }

  return (
    <Styles.MainContainer>
      {isDisabledByGlobalSettings && <GlobalSettingsInUseBanner />}
      <Styles.TitleAndDescriptionContainer>
        <InputWithLabel
          label={t('Nombre de la plantilla')}
          value={name}
          onChange={handleChangeName}
          disabled={isDisabledByGlobalSettings}
        />
        <TextArea
          placeholder={t('Descripción de la plantilla')}
          value={description}
          onChange={handleChangeDescription}
          disabled={isDisabledByGlobalSettings}
        />
      </Styles.TitleAndDescriptionContainer>
      <Divider />
      <Styles.EmailContentContainer>
        <EditorModeSelector
          mode={selectedEditorMode}
          onChangeMode={handleSelectEditorMode}
          smartTrackingEnabled={features?.smart_tracking_enabled}
          isCustomHtmlUpsellShown={isCustomHtmlUpsellShown}
          disabled={isDisabledByGlobalSettings}
        />
        {isCustomHtmlUpsellShown && <CustomHtmlUpsell onBackToStandardEditor={handleBackToStandardEditor} />}
        {!isCustomHtmlUpsellShown && (
          <>
            <LanguageTabs
              availableLanguages={availableLanguages}
              languageSelected={languageSelected}
              onChangeLanguage={handleChangeLanguage}
            />
            <Subject
              subject={subject}
              onChangeSubject={handleChangeSubject}
              languageSelected={languageSelected}
              availableVariables={availableVariables}
              availableSections={availableSections}
              disabled={isDisabledByGlobalSettings}
            />
            {!isLiquidMode && (
              <Preheader
                key={languageSelected}
                preheader={preheader}
                onChangePreheader={handleChangePreheader}
                availableVariables={availableVariables}
                availableSections={availableSections}
                disabled={isDisabledByGlobalSettings}
              />
            )}
            <ContentTabs
              mode={mode}
              selectedContentTab={selectedContentTab}
              onChangeContentTab={setSelectedContentTab}
            />
            {selectedContentTab === contentTabs.write && isLiquidMode && (
              <Suspense fallback={<Skeleton variant="rounded" width="100%" height={liquidEditorHeight} />}>
                <LiquidEditor
                  content={content}
                  onChangeContent={handleChangeLiquidContent}
                  languageSelected={languageSelected}
                  liquidError={liquidError}
                  disabled={isDisabledByGlobalSettings}
                />
              </Suspense>
            )}
            {selectedContentTab === contentTabs.write && !isLiquidMode && (
              <Content
                content={content}
                onChangeContent={handleChangeContent}
                languageSelected={languageSelected}
                availableVariables={availableVariables}
                availableSections={availableSections}
                disabled={isDisabledByGlobalSettings}
                merchantId={info?.id}
              />
            )}
            {selectedContentTab === contentTabs.contentBlocks && (
              <ContentBlocks
                blocks={blocks}
                onChangeBlocks={handleChangeBlocks}
                disabled={isDisabledByGlobalSettings}
              />
            )}
            {selectedContentTab === contentTabs.preview && isLiquidMode && (
              <LiquidPreview
                preview={preview}
                fetchingPreview={fetchingPreview}
                liquidError={liquidPreviewError}
                isLiquidContentEmpty={isLiquidContentEmpty}
                languageSelected={languageSelected}
              />
            )}
            {selectedContentTab === contentTabs.preview && !isLiquidMode && (
              <Preview preview={preview?.content} fetchingPreview={fetchingPreview} />
            )}
          </>
        )}
      </Styles.EmailContentContainer>
      <Trigger
        triggers={triggers}
        onChangeTrigger={handleChangeTrigger}
        onAddTrigger={handleAddTrigger}
        onRemoveTrigger={handleRemoveTrigger}
        disabled={isDisabledByGlobalSettings}
        disabledTriggers={disabledTriggers}
        errors={errors}
      />
      <EmailTemplateConditionsEditor
        conditions={conditions}
        errors={conditionErrors}
        onChange={handleChangeConditions}
        disabled={isDisabledByGlobalSettings}
        productTypes={productTypes}
        productCategories={productCategories}
        productCollections={productCollections}
        regions={regions}
        locations={locations}
        carriers={carriers}
        carrierServices={carrierServices}
        returnReasons={returnReasons}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        disabledConditionFields={disabledConditionFields}
      />
      <SaveChangesButtons
        onSaveChanges={onSaveChanges}
        hasChanges={hasChanges}
        onRevertChanges={onCancelChanges}
        onSave={onSaveChanges}
        loading={editingDetailEmailTemplate}
        disabled={editingDetailEmailTemplate || !isFormValid()}
        defaultLabel={isNewTemplate ? t('Crear') : undefined}
      />
    </Styles.MainContainer>
  )
}

export default EmailTemplateDetailConfiguration
